/**
 * Start an agreement — the first write surface in Hotel Supplier Management.
 *
 * A ROUTE (`/hotel-supplier/agreements/new`), not a modal. It carries ~20 fields
 * across four groups, which is cramped in a dialog, and being addressable means a
 * half-filled form survives a refresh being shared or bookmarked. Modals are
 * reserved for confirming side-effectful transitions (don't-renew, reopen), where
 * the whole point is to interrupt.
 *
 * ── Two POSTs, on purpose ───────────────────────────────────────────────────
 * An agreement needs a `property_id`, and a property may not exist yet — the
 * team's first records will mostly be hotels nobody has entered. So the property
 * step offers both "pick an existing one" and "add a new one", and on submit
 * creates the property first, then the agreement with the returned id.
 *
 * That ordering matters for failure: if the property POST succeeds and the
 * agreement POST fails, a property row is left behind. That is deliberate and
 * stated in the error — an orphan property is harmless (it is a sourcing record
 * that can be reused or archived), whereas rolling it back would need a delete
 * endpoint and could destroy a row someone else had started using in between.
 *
 * ── Validation is the API's, not a second copy ───────────────────────────────
 * The client checks only what it needs to keep the form usable (required fields,
 * and that a term does not run backwards). Everything else — decimal precision,
 * email casing, impossible dates — is enforced by `parseAgreementCreate` on the
 * server, and its message is surfaced verbatim. A second set of rules here would
 * drift from those and start rejecting things the API accepts, or worse, accepting
 * things it does not.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  AgreementDropzone,
  AgreementEntryChoice,
  DerivedMark,
  ExtractionSummary,
  OverriddenMark,
} from '@/features/hotel-supplier/components/AgreementUpload';
import {
  applyExtraction,
  type ExtractedField,
  type ExtractionResult,
} from '@/features/hotel-supplier/lib/extraction';
import { CustomerSearchSelect } from '@/features/hotel-supplier/components/CustomerSearchSelect';
import { AmenitiesModal } from '@/features/hotel-supplier/components/AmenitiesModal';
import { AgreementFields } from '@/features/hotel-supplier/components/AgreementFields';
import {
  blankAnswers,
  toPayload as amenitiesToPayload,
  type AmenityAnswers,
} from '@/features/hotel-supplier/amenities';
import { FilterableSelect } from '@/features/hotel-supplier/components/FilterableSelect';

interface Customer {
  customer_id: string;
  name: string;
}

interface Property {
  id: string;
  propertyName: string;
  propertyCity: string | null;
  propertyState: string | null;
  /*
   * Present in the response already — `GET /api/btp/properties` does a
   * `findMany` with no `select` and spreads the row — but not declared here
   * until the hotel filter needed them. Declared rather than fetched: typing a
   * Sabre code should find the hotel even though the option label does not show
   * one, and the value was on the wire the whole time.
   */
  gdsPropertyCode?: string | null;
  vendorCode?: string | null;
}

const NEW_PROPERTY = '__new__';

export default function NewAgreementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-selecting a customer via ?customer_id= is what makes this reachable from a
  // customer's own page later without re-choosing them.
  const [customerId, setCustomerId] = useState(
    searchParams.get('customer_id') ?? '',
  );
  const [propertyId, setPropertyId] = useState('');

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // New-property fields, used only when propertyId === NEW_PROPERTY.
  const [np, setNp] = useState({
    property_name: '',
    property_city: '',
    property_state: '',
    vendor_family: '',
    vendor_code: '',
    gds_property_code: '',
  });

  const [form, setForm] = useState({
    status: 'rfp_created',
    owner_team: 'sourcing',
    tmc: '',
    negotiation_id: '',
    term_start: '',
    term_end: '',
    negotiated_currency: 'USD',
    commission_rate_pct: '',
    airport_codes: '',
    estimated_annual_room_nights: '',
    avg_length_of_stay_requested: '',
    lowest_fixed_rate_requested: '',
    dynamic_discount_pct_requested: '',
    hsm_name: '',
    hsm_email: '',
    hsm_phone: '',
    hotel_poc_name: '',
    hotel_poc_email: '',
    internal_note: '',
    hotel_note: '',
  });

  /*
   * The RFP's amenity and policy answers, held apart from `form`.
   *
   * `form` is a flat map of scalar columns; this is one jsonb value with its own
   * question list, and merging them would mean `compact(form)` deciding what
   * "blank" means for thirteen questions whose blank rules differ (a hidden
   * conditional answer must not be sent at all). `amenities.toPayload` owns
   * that, and the two stay separate all the way to the POST body.
   */
  const [amenities, setAmenities] = useState<AmenityAnswers>(blankAnswers());
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Which entry path the user picked.
   *
   * 'choose' is the landing state, so neither path is imposed. `?customer_id=`
   * still lands on the choice rather than skipping it — arriving from a
   * customer's page tells us WHO, not HOW.
   */
  const [entry, setEntry] = useState<'choose' | 'manual' | 'upload'>('choose');

  // Extraction provenance. `derived` is what the document proposed; `overridden`
  // is what a human has since replaced. Both are display-only — `form` is the
  // single source of what will actually be submitted, so a bug here can mislabel
  // a value but can never change one.
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [uploadedName, setUploadedName] = useState('');
  const [blobPath, setBlobPath] = useState<string | null>(null);
  /** The upload was stored but unreadable — drives the note on the manual form. */
  const [savedUnread, setSavedUnread] = useState(false);
  const [derived, setDerived] = useState<Record<string, ExtractedField>>({});
  const [overridden, setOverridden] = useState<Record<string, true>>({});

  const set = (k: keyof typeof form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Feedback log §27: "record an override when a human replaces a derived
    // one". Recorded on the first edit and kept even if they type the original
    // value back — the point is that a person looked at it and took ownership,
    // which stays true regardless of where they landed.
    setDerived((d) => {
      if (!(k in d)) return d;
      setOverridden((o) => ({ ...o, [k]: true }));
      const { [k]: _removed, ...rest } = d;
      return rest;
    });
  };

  /** The provenance marker for a field, or nothing for a hand-typed one. */
  const mark = (k: keyof typeof form) =>
    overridden[k] ? <OverriddenMark /> : <DerivedMark field={derived[k]} />;

  function onExtracted(
    res: { blob_path: string; extraction: ExtractionResult },
    filename: string,
  ) {
    const applied = applyExtraction(form, res.extraction.fields);
    setForm(applied.form);
    setDerived(applied.derived);
    setOverridden({});
    setBlobPath(res.blob_path);
    setExtraction(res.extraction);
    setUploadedName(filename);

    // The server resolves the client and hotel NAMES to ids against what the
    // caller can see, so these arrive as ordinary field values — but they drive
    // the two pickers rather than inputs, and setting the customer is what
    // triggers the property fetch.
    const cid = res.extraction.fields.customer_id?.value;
    if (cid) setCustomerId(cid);
    const pid = res.extraction.fields.property_id?.value;
    if (pid) setPendingPropertyId(pid);

    setEntry('manual');
  }

  /**
   * The document was saved but could not be read — `extraction_failed`.
   *
   * Everything `onExtracted` does EXCEPT apply fields, because there are none. The
   * load-bearing line is `setBlobPath`: the create submit spreads
   * `contract_blob_path` only when it is set, so without this the reviewer types the
   * whole form and the agreement is created with no attachment while the uploaded
   * document orphans in the container.
   *
   * `savedUnread` drives an on-screen note. The banner already told them the file is
   * attached; if the manual form then looks identical to a from-scratch one, the only
   * evidence of that is a sentence they have already dismissed.
   */
  function onSavedUnread(path: string, filename: string) {
    setBlobPath(path);
    setUploadedName(filename);
    setSavedUnread(true);
    setEntry('manual');
  }

  /**
   * Drop the attachment but STAY on the form.
   *
   * Deliberately not `discardUpload`, which also resets `entry` to 'choose' and throws
   * away the extraction state — right for "start over", wrong here. Someone who
   * uploaded the wrong file still wants the agreement they were part-way through
   * typing; sending them back to the choice screen would cost that work.
   *
   * The blob itself is left in the container. It is attributable (`uploadedBy` /
   * `uploadedAt` metadata) and the container's lifecycle policy sweeps unreferenced
   * uploads, which is the same call the route header already documents for an
   * abandoned review — a request-path delete is not this page's job.
   */
  function detachSavedUpload() {
    setBlobPath(null);
    setUploadedName('');
    setSavedUnread(false);
  }

  /**
   * A property id proposed by extraction, held until its customer's property
   * list has loaded.
   *
   * Needed because changing the customer CLEARS the property selection (that
   * guard is what stops you submitting customer A with customer B's hotel), so
   * setting both at once would have the clear win the race and silently drop the
   * hotel the document named.
   */
  const [pendingPropertyId, setPendingPropertyId] = useState<string | null>(
    null,
  );

  function discardUpload() {
    setExtraction(null);
    setDerived({});
    setOverridden({});
    setBlobPath(null);
    setUploadedName('');
    // Cleared with `blobPath`, not separately: the note ASSERTS an attachment, so
    // leaving it set after a discard would promise a file the submit no longer sends.
    setSavedUnread(false);
    setEntry('choose');
  }

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ customers: Customer[] }>('/api/customers')
      .then((d) => {
        if (!cancelled) setCustomers(d.customers);
      })
      .catch(() => {
        // A failed customer list is not fatal — the id can still be typed. Surface
        // it only if the user has no way forward.
        if (!cancelled) setCustomers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Properties are per-customer, so this refetches whenever the customer changes —
  // and clears the selection, or you would submit customer A with customer B's
  // property and get the API's ownership 404 with no idea why.
  useEffect(() => {
    if (!customerId) {
      setProperties([]);
      setPropertyId('');
      return;
    }
    let cancelled = false;
    setLoadingProperties(true);
    setPropertyId('');
    apiFetch<{ properties: Property[] }>(
      `/api/btp/properties?customer_id=${encodeURIComponent(customerId)}`,
    )
      .then((d) => {
        if (!cancelled) setProperties(d.properties);
      })
      .catch(() => {
        if (!cancelled) setProperties([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProperties(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // Apply an extracted hotel only once its customer's list has arrived, and only
  // if that hotel is actually in it. A proposed id that is not in the list would
  // otherwise sit in state as a selection the dropdown cannot show — looking
  // chosen while submitting nothing.
  useEffect(() => {
    if (!pendingPropertyId || properties.length === 0) return;
    if (properties.some((p) => p.id === pendingPropertyId)) {
      setPropertyId(pendingPropertyId);
    }
    setPendingPropertyId(null);
  }, [properties, pendingPropertyId]);

  /**
   * Only what the form needs to stay usable. Everything else is the server's
   * job — see the module header.
   */
  const customerError = useMemo(() => {
    if (!customerId) return 'Choose a customer.';
    if (!propertyId) return 'Choose a hotel, or add a new one.';
    if (propertyId === NEW_PROPERTY && !np.property_name.trim()) {
      return 'A new hotel needs a name.';
    }
    /*
     * A NEW HOTEL NEEDS A GDS CODE — item 56, and now the API's rule too.
     *
     * `POST /api/btp/properties` refuses (422) when the code does not resolve to
     * exactly one `hotel_master` row, because a property with no
     * `hotel_canonical_id` can never appear in the Hotels tab and cannot be
     * reconciled afterwards. Jeff found that by adding a hotel and then failing
     * to find it.
     *
     * Checked here as well as there so the user learns it while the form is
     * still in front of them, rather than after a round trip. The server stays
     * the authority — this only avoids offering a submit that cannot succeed.
     */
    if (propertyId === NEW_PROPERTY && !np.gds_property_code.trim()) {
      return (
        'A new hotel needs its GDS property code — without one it cannot be ' +
        'linked to the hotel master, and it would not show up in the Hotels tab.'
      );
    }
    if (form.term_start && form.term_end && form.term_end < form.term_start) {
      return 'The term cannot end before it starts.';
    }
    return null;
  }, [
    customerId,
    propertyId,
    np.property_name,
    np.gds_property_code,
    form.term_start,
    form.term_end,
  ]);

  /** Drop blanks so the API sees absent fields rather than empty strings. */
  const compact = (o: Record<string, string>) =>
    Object.fromEntries(
      Object.entries(o).filter(([, v]) => v.trim() !== ''),
    ) as Record<string, string>;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (customerError || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      let resolvedPropertyId = propertyId;

      if (propertyId === NEW_PROPERTY) {
        const created = await apiFetch<{ property: { id: string } }>(
          '/api/btp/properties',
          {
            method: 'POST',
            body: JSON.stringify({
              customer_id: customerId,
              status: 'identified',
              ...compact(np),
            }),
          },
        );
        resolvedPropertyId = created.property.id;
      }

      const created = await apiFetch<{ agreement: { id: string } }>(
        '/api/btp/agreements',
        {
          method: 'POST',
          body: JSON.stringify({
            customer_id: customerId,
            property_id: resolvedPropertyId,
            ...compact(form),
            /*
             * OMITTED ENTIRELY when nothing was answered — `toPayload` returns
             * undefined rather than `{}`, and `JSON.stringify` drops an
             * undefined value. An empty object would be a write, replacing a
             * blob that a renewal had carried forward.
             */
            ...(amenitiesToPayload(amenities)
              ? { amenities: amenitiesToPayload(amenities) }
              : {}),
            // Both derived from the SAME fact — that a document was uploaded —
            // so they cannot disagree. F13 requires the flag for reporting: an
            // externally negotiated agreement has no ask of ours to compare
            // against, and counting one in "how far did the negotiation move"
            // corrupts the metric.
            ...(blobPath
              ? { contract_blob_path: blobPath, externally_negotiated: true }
              : {}),
          }),
        },
      );

      // Land on the stage this agreement is actually in, so the new row is
      // visible rather than the user wondering where it went.
      const stage = form.status === 'loading' ? 'loading' : 'sourcing';
      navigate(
        `/hotel-supplier/agreements?stage=${stage}&created=${encodeURIComponent(created.agreement.id)}`,
      );
    } catch (err) {
      // Surface the API's own message. It is the authority on what was wrong,
      // and paraphrasing it here would hide precision the server has.
      const base =
        err instanceof ApiError
          ? typeof err.body === 'object' &&
            err.body !== null &&
            'error' in err.body
            ? String((err.body as { error: unknown }).error)
            : err.message
          : String(err);
      setError(
        propertyId === NEW_PROPERTY
          ? `${base} — note the new hotel may already have been created; check the list before adding it again.`
          : base,
      );
      setSubmitting(false);
    }
  }

  const creatingProperty = propertyId === NEW_PROPERTY;

  return (
    <div className="page" data-testid="supplier-agreement-new-page">
      <div className="page-header">
        <h1>Start an agreement</h1>
        <p className="page-intro">
          Only the customer and the hotel are required — an agreement
          legitimately begins as a near-empty sourcing record and fills in as
          the negotiation runs. Everything here can be edited later.
        </p>
      </div>

      {entry === 'choose' && (
        <AgreementEntryChoice
          onManual={() => setEntry('manual')}
          onUpload={() => setEntry('upload')}
        />
      )}

      {entry === 'upload' && (
        <AgreementDropzone
          onBack={() => setEntry('manual')}
          onExtracted={onExtracted}
          onSavedUnread={onSavedUnread}
        />
      )}

      {/* The evidence behind "the document is already attached". Without it the
          manual form is indistinguishable from a from-scratch one, and the only
          record of the attachment is a banner the reviewer has already dismissed.

          IT CARRIES ITS OWN DETACH, and that gap was found by the test below rather
          than by reading. The existing `supplier-agreement-discard-upload` lives
          inside `ExtractionSummary`, which renders only when there IS an extraction —
          so on this path a document uploaded by accident had no way off the form
          short of leaving the page, and it would have been bound to the agreement on
          submit. An attachment the reviewer cannot remove is worse than one they have
          to add. */}
      {savedUnread && (
        <p className="detail-note" data-testid="agreement-saved-unread">
          <span className="muted">
            <strong>{uploadedName}</strong> is uploaded and will be attached to
            this agreement — nothing could be read out of it automatically, so
            the fields below are yours to fill in. There is no need to upload it
            again.
          </span>{' '}
          <button
            className="btn btn-sm"
            data-testid="agreement-saved-unread-detach"
            onClick={detachSavedUpload}
            type="button"
          >
            Don&rsquo;t attach it
          </button>
        </p>
      )}

      {extraction && (
        <ExtractionSummary
          extraction={extraction}
          filename={uploadedName}
          onDiscard={discardUpload}
        />
      )}

      {/* The form is MOUNTED but hidden until a path is chosen, rather than
          unmounted. Unmounting would reset every field, which is the difference
          between "start over with a blank form" being a deliberate button and
          being something an accidental click on the entry choice does to work
          already in progress. */}
      <form hidden={entry !== 'manual'} onSubmit={onSubmit}>
        <div className="section">
          <div className="section-header">
            <span className="section-label">Customer &amp; hotel</span>
          </div>
          <div className="section-body">
            <div className="form-grid">
              <div>
                <label className="field-label" htmlFor="na-customer">
                  Customer *
                </label>
                {/*
                 * SEARCH, NOT A DROPDOWN — item 54, and it was hiding customers
                 * rather than merely being tedious.
                 *
                 * This was a `<select>` fed by `GET /api/customers` with no `q`
                 * and no `limit`, and that endpoint defaults to **limit=50**. So
                 * the list was the first fifty customers alphabetically with no
                 * indication that anything followed: anyone sorting later than
                 * the fiftieth could not be chosen at all. Jeff hit it on
                 * staging with real data.
                 *
                 * The replacement searches SERVER-SIDE and says when its list is
                 * a page. See CustomerSearchSelect for why it cannot be a
                 * browser filter.
                 */}
                <CustomerSearchSelect
                  id="na-customer"
                  onChange={setCustomerId}
                  testId="supplier-agreement-new-customer"
                  value={customerId}
                  valueName={
                    customers.find((c) => c.customer_id === customerId)?.name
                  }
                />
              </div>
              <div>
                <label className="field-label" htmlFor="na-property">
                  Hotel *
                </label>
                {/*
                 * A LOCAL filter here, deliberately unlike the customer field
                 * above. This customer's properties arrive in ONE unpaginated
                 * response, so every option is already in the browser and
                 * filtering locally searches all of them. The customer base does
                 * not, which is why that one asks the server. The asymmetry is
                 * the data, not an inconsistency — see FilterableSelect.
                 */}
                <FilterableSelect
                  disabled={!customerId || loadingProperties}
                  extraOptions={
                    customerId
                      ? [{ value: NEW_PROPERTY, label: '+ Add a new hotel…' }]
                      : []
                  }
                  id="na-property"
                  onChange={setPropertyId}
                  options={properties.map((p) => ({
                    value: p.id,
                    label: [
                      p.propertyName,
                      p.propertyCity ? ` — ${p.propertyCity}` : '',
                      p.propertyState ? `, ${p.propertyState}` : '',
                    ].join(''),
                    // Typing a Sabre code should find the hotel even though the
                    // label does not show it.
                    keywords: [p.gdsPropertyCode, p.vendorCode]
                      .filter(Boolean)
                      .join(' '),
                  }))}
                  placeholder={
                    !customerId
                      ? 'Choose a customer first…'
                      : loadingProperties
                        ? 'Loading hotels…'
                        : 'Choose a hotel…'
                  }
                  testId="supplier-agreement-new-property"
                  value={propertyId}
                />
              </div>
            </div>

            {/* Revealed only when adding a hotel. Kept inline rather than on a
                separate page: needing to leave a half-filled agreement to go
                create a property is how the first record never gets entered. */}
            {creatingProperty && (
              <div
                data-testid="supplier-agreement-new-property-fields"
                style={{ marginTop: 16 }}
              >
                <div className="form-grid">
                  <div>
                    <label className="field-label" htmlFor="np-name">
                      Hotel name *
                    </label>
                    <input
                      className="input"
                      data-testid="supplier-agreement-new-property-name"
                      id="np-name"
                      onChange={(e) =>
                        setNp((n) => ({ ...n, property_name: e.target.value }))
                      }
                      value={np.property_name}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="np-city">
                      City
                    </label>
                    <input
                      className="input"
                      id="np-city"
                      onChange={(e) =>
                        setNp((n) => ({ ...n, property_city: e.target.value }))
                      }
                      value={np.property_city}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="np-state">
                      State
                    </label>
                    <input
                      className="input"
                      id="np-state"
                      maxLength={2}
                      onChange={(e) =>
                        setNp((n) => ({ ...n, property_state: e.target.value }))
                      }
                      value={np.property_state}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="np-family">
                      Chain family
                    </label>
                    <input
                      className="input"
                      id="np-family"
                      onChange={(e) =>
                        setNp((n) => ({ ...n, vendor_family: e.target.value }))
                      }
                      placeholder="Marriott"
                      value={np.vendor_family}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="np-vendor">
                      Vendor code
                    </label>
                    <input
                      className="input"
                      id="np-vendor"
                      onChange={(e) =>
                        setNp((n) => ({ ...n, vendor_code: e.target.value }))
                      }
                      placeholder="MC"
                      value={np.vendor_code}
                    />
                  </div>
                  <div>
                    {/*
                     * REQUIRED, as of item 55/56 — this is the field that
                     * decides whether the property can be linked to the hotel
                     * master at all, and an unlinked property is invisible in
                     * the Hotels tab and unreconcilable later.
                     *
                     * NO GDS PICKER, deliberately, despite the plan's wording.
                     * `resolveHotelCanonicalId` looks the code up across ALL
                     * THREE master columns — Sabre, Travelport and Worldspan —
                     * so which system it came from does not change the lookup.
                     * A dropdown that cannot affect the outcome is a question
                     * asked for its own sake; saying what we match against is
                     * the useful half of "say WHICH GDS".
                     */}
                    <label className="field-label" htmlFor="np-gds">
                      GDS property code{' '}
                      <span style={{ fontWeight: 400 }}>(required)</span>
                    </label>
                    <input
                      aria-describedby="np-gds-help"
                      className="input mono"
                      data-testid="supplier-agreement-new-property-gds"
                      id="np-gds"
                      onChange={(e) =>
                        setNp((n) => ({
                          ...n,
                          gds_property_code: e.target.value,
                        }))
                      }
                      placeholder="MC12345"
                      required
                      value={np.gds_property_code}
                    />
                    <p
                      className="detail-note"
                      id="np-gds-help"
                      style={{ fontSize: 11, margin: '4px 0 0' }}
                    >
                      <span className="muted">
                        Matched against the hotel master&rsquo;s Sabre,
                        Travelport and Worldspan codes. If it doesn&rsquo;t
                        match exactly one property, the hotel can&rsquo;t be
                        added yet.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/*
         * The field groups themselves live in AgreementFields, shared with the
         * drawer's edit modal so twenty fields exist in one place.
         *
         * `set` and `mark` are widened at the boundary: this page types its
         * form keys as a union (which is what makes `set('tmc', …)` typo-proof
         * here) while the shared component takes `string`, because it renders
         * the same keys for a host that reads them off an API response. The
         * cast is sound by construction — the component only ever passes keys
         * it itself renders — and narrowing it any other way would mean
         * exporting the union and coupling the two hosts to one field list at
         * the type level, which is the coupling the extraction removed.
         */}
        <AgreementFields
          amenities={amenities}
          form={form}
          mark={(k) => mark(k as keyof typeof form)}
          onOpenAmenities={() => setAmenitiesOpen(true)}
          set={(k, v) => set(k as keyof typeof form, v)}
          showStatus
        />

        {amenitiesOpen && (
          <AmenitiesModal
            answers={amenities}
            onClose={() => setAmenitiesOpen(false)}
            onSave={(next) => {
              setAmenities(next);
              setAmenitiesOpen(false);
            }}
            testId="na-amenities"
          />
        )}

        {error && (
          <div
            className="banner banner-error"
            data-testid="supplier-agreement-new-error"
            role="alert"
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            marginTop: 16,
          }}
        >
          <button
            className="btn btn-primary"
            data-testid="supplier-agreement-new-submit"
            disabled={submitting || customerError !== null}
            type="submit"
          >
            {submitting ? 'Saving…' : 'Start agreement'}
          </button>
          <Link className="btn btn-outline" to="/hotel-supplier/agreements">
            Cancel
          </Link>
          {/* The reason the button is disabled, rather than a dead control the
              user has to guess about. */}
          {customerError && (
            <span
              data-testid="supplier-agreement-new-hint"
              style={{ fontSize: 12.5, color: 'var(--text-muted)' }}
            >
              {customerError}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
